import {
	Button,
	RiAlertLine,
	RiArrowUpDownLine,
	RiChat4Line,
	RiDeleteBin2Fill,
	RiQuillPenFill
} from "@itandi/itandi-bb-ui";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import React from "react";
import { SortableHandle } from "react-sortable-hoc";
import { toast } from "react-toastify";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { NskResponse } from "~/models/Api";
import type { Survey, SurveyOption } from "~/models/Survey";
import { PATH as SURVEY_SHOW } from "~/pages/vendors/settings/surveys/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { formatDate } from "~/utils/DateUtil";
import Styles from "./SurveyList.module.scss";

type Props = {
	canActiveSurvey: boolean;
	handleActivate: (surveyId: number) => Promise<NskResponse>;
	handleChangeEnabled: (
		surveyId: number,
		value: boolean
	) => Promise<NskResponse>;
	handleDestroy: (surveyId: number) => Promise<NskResponse>;
	survey: Survey;
};

export const SurveyListRow: React.FC<Props> = ({
	canActiveSurvey,
	survey,
	handleActivate,
	handleChangeEnabled,
	handleDestroy
}: Props) => {
	const renderOption = React.useCallback(
		(surveyOption: SurveyOption): ReactNode => (
			<div key={surveyOption.id}>
				{surveyOption.text}({surveyOption.value > 0 && "+"}
				{surveyOption.value})
			</div>
		),
		[]
	);

	// 一時停止/再開処理
	const [changeEnabledSubmitting, setChangeEnabledSubmitting] =
		React.useState<boolean>(false);
	const {
		isOpen: isChangeEnabledModalOpen,
		handleOpen: handleChangeEnabledModalOpen,
		handleClose: handleChangeEnabledModalClose
	} = useOpenHandler();
	const handleChangeEnabledSubmit = React.useCallback(async () => {
		setChangeEnabledSubmitting(true);
		const result = await handleChangeEnabled(survey.id, !survey.enabled);
		handleChangeEnabledModalClose();
		setChangeEnabledSubmitting(false);
		if (isSuccess(result)) {
			toast.success("更新しました");
			return;
		}
		toast.error(`エラーが発生しました。${result.message}`);
	}, [
		handleChangeEnabled,
		handleChangeEnabledModalClose,
		survey.enabled,
		survey.id
	]);

	// 有効化処理
	const [activateSubmitting, setActivateSubmitting] =
		React.useState<boolean>(false);
	const {
		isOpen: isActivateModalOpen,
		handleOpen: handleActivateModalOpen,
		handleClose: handleActivateModalClose
	} = useOpenHandler();
	const handleActivateSubmit = React.useCallback(async () => {
		setActivateSubmitting(true);
		const result = await handleActivate(survey.id);
		handleActivateModalClose();
		if (isSuccess(result)) {
			toast.success("有効にしました");
			return;
		}
		setActivateSubmitting(false);
		toast.error(`エラーが発生しました。${result.message}`);
	}, [handleActivate, handleActivateModalClose, survey.id]);

	// 削除処理
	const [destroySubmitting, setDestroySubmitting] =
		React.useState<boolean>(false);
	const {
		isOpen: isDeleteModalOpen,
		handleOpen: handleDeleteModalOpen,
		handleClose: handleDeleteModalClose
	} = useOpenHandler();
	const handleDestroySubmit = React.useCallback(async () => {
		setDestroySubmitting(true);
		const result = await handleDestroy(survey.id);
		if (isSuccess(result)) {
			toast.success("削除しました");
			return;
		}
		setDestroySubmitting(false);
		toast.error(`エラーが発生しました。${result.message}`);
	}, [handleDestroy, survey.id]);

	const DragHandle = SortableHandle(() => (
		<div>
			<RiArrowUpDownLine />
		</div>
	));

	const router = useRouter();
	const onClickEdit = React.useCallback(() => {
		router.push(SURVEY_SHOW(survey.id)).then();
	}, [router, survey.id]);
	return (
		<tr className={Styles.ListRow}>
			<td className={Styles.ListCol}>
				<DragHandle />
			</td>
			<td className={Styles.ListCol}>
				{survey.activatedAt === null && <span>下書き</span>}
				{survey.activatedAt !== null && !survey.enabled && (
					<>
						<RiAlertLine className={Styles.icon} />
						<span>一時停止</span>
					</>
				)}
				{survey.activatedAt !== null && survey.enabled ? (
					<span>有効</span>
				) : null}
			</td>
			<td className={Styles.ListCol}>
				<div>{survey.title}</div>
				<div className={Styles.SubLabel}>{survey.description}</div>
			</td>
			<td className={Styles.ListCol}>
				<div>{survey.surveyOptions.map(renderOption)}</div>
			</td>
			<td className={Styles.ListCol}>
				{survey.enabledNote ? <RiChat4Line className={Styles.icon} /> : ``}
			</td>
			<td className={Styles.ListCol}>
				{survey.activatedAt === null && (
					<Button height="small" onClick={onClickEdit} variant="secondary">
						編集する
					</Button>
				)}
				{survey.activatedAt !== null && survey.enabled ? (
					<Button
						disabled={changeEnabledSubmitting}
						height="small"
						onClick={handleChangeEnabledModalOpen}
						variant="primary"
					>
						一時停止
					</Button>
				) : null}
				{survey.activatedAt !== null && !survey.enabled && canActiveSurvey ? (
					<Button
						disabled={changeEnabledSubmitting}
						height="small"
						onClick={handleChangeEnabledModalOpen}
						variant="primary"
					>
						有効化
					</Button>
				) : null}
				<ConfirmModal
					confirmType="save"
					handleAccept={handleChangeEnabledSubmit}
					handleCancel={handleChangeEnabledModalClose}
					isOpen={isChangeEnabledModalOpen}
				>
					アンケートを{survey.enabled ? "一時停止に" : "有効に"}しますか？
				</ConfirmModal>
			</td>
			<td className={Styles.ListCol}>
				{survey.activatedAt === null && canActiveSurvey ? (
					<Button
						disabled={activateSubmitting}
						onClick={handleActivateModalOpen}
						startIcon={<RiQuillPenFill className={Styles.Icon} />}
						variant="tertiary"
					>
						有効化
					</Button>
				) : null}
				{survey.activatedAt !== null && (
					<div>{formatDate(survey.activatedAt)}</div>
				)}
				<ConfirmModal
					confirmType="save"
					handleAccept={handleActivateSubmit}
					handleCancel={handleActivateModalClose}
					isOpen={isActivateModalOpen}
				>
					アンケートを有効にしますか？
					<br />
					一度有効にするとアンケート内容は編集できなくなります。 <br />
					この操作は元に戻すことができません。
				</ConfirmModal>
			</td>
			<td className={Styles.ListCol}>
				<Button
					disabled={destroySubmitting}
					onClick={handleDeleteModalOpen}
					startIcon={<RiDeleteBin2Fill className={Styles.Icon} />}
					variant="iconTransparent"
				>
					削除
				</Button>
				<ConfirmModal
					confirmType="delete"
					handleAccept={handleDestroySubmit}
					handleCancel={handleDeleteModalClose}
					isOpen={isDeleteModalOpen}
				>
					アンケートを削除してもよろしいですか？
					<br />
					回答されたデータも削除されます。 <br />
					この操作は元に戻すことができません。
				</ConfirmModal>
			</td>
		</tr>
	);
};
